import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { InfoIcon } from './icons/InfoIcon'
import { InfoTooltip } from './InfoTooltip'
import { AgentSummaryField } from './AgentSummaryField'
import type { SkillType } from '../lib/types'
import { MdEditor } from './MdEditor'

export interface SkillFormValue {
  name: string
  type: SkillType
  shortDescription: string
  agentSummary: string
  definition: string
}

export type SkillFieldErrors = Partial<Record<'name' | 'shortDescription' | 'agentSummary' | 'definition', string>>

type SkillErrorKey =
  | 'skillsTab.errors.nameRequired'
  | 'skillsTab.errors.descriptionRequired'
  | 'skillsTab.errors.agentSummaryRequired'
  | 'skillsTab.errors.definitionRequired'

export function getSkillFormErrors(value: SkillFormValue): Partial<Record<'name' | 'shortDescription' | 'agentSummary' | 'definition', SkillErrorKey>> {
  const errors: Partial<Record<'name' | 'shortDescription' | 'agentSummary' | 'definition', SkillErrorKey>> = {}
  if (!value.name.trim()) errors.name = 'skillsTab.errors.nameRequired'
  if (!value.shortDescription.trim()) errors.shortDescription = 'skillsTab.errors.descriptionRequired'
  if (!value.agentSummary.trim()) errors.agentSummary = 'skillsTab.errors.agentSummaryRequired'
  if (!value.definition.trim()) errors.definition = 'skillsTab.errors.definitionRequired'
  return errors
}

// Field order here drives error-priority for both the returned error map and
// focusFirstSkillFormError's "which invalid field to focus" choice below.
const SKILL_FIELD_ID_SUFFIX = {
  name: 'name',
  shortDescription: 'shortdesc',
  agentSummary: 'agentsummary',
  definition: 'definition',
} as const

type SkillErrorKeys = ReturnType<typeof getSkillFormErrors>

export function buildSkillFieldErrors(errorKeys: SkillErrorKeys, t: TFunction<'translation', undefined>): SkillFieldErrors {
  const errors: SkillFieldErrors = {}
  for (const field of Object.keys(SKILL_FIELD_ID_SUFFIX) as (keyof typeof SKILL_FIELD_ID_SUFFIX)[]) {
    const key = errorKeys[field]
    if (key) errors[field] = t(key)
  }
  return errors
}

export function focusFirstSkillFormError(errorKeys: SkillErrorKeys, idPrefix: string): void {
  setTimeout(() => {
    for (const field of Object.keys(SKILL_FIELD_ID_SUFFIX) as (keyof typeof SKILL_FIELD_ID_SUFFIX)[]) {
      if (errorKeys[field]) {
        document.getElementById(`${idPrefix}-${SKILL_FIELD_ID_SUFFIX[field]}`)?.focus()
        return
      }
    }
  }, 0)
}

function FieldErrorMessage({ message }: { message: string }) {
  return (
    <div className="field-error" role="alert">
      <InfoIcon size={12} />
      {message}
    </div>
  )
}

interface SkillFormProps {
  value: SkillFormValue
  onChange: (patch: Partial<SkillFormValue>) => void
  idPrefix: string
  namePlaceholder?: string
  errors?: SkillFieldErrors
  readOnlyAgent?: boolean
}

export function SkillForm({ value, onChange, idPrefix, namePlaceholder, errors = {}, readOnlyAgent = false }: Readonly<SkillFormProps>) {
  const { t } = useTranslation()

  return (
    <>
      <div className={`form-group${errors.name ? ' form-group-invalid' : ''}`}>
        <label htmlFor={`${idPrefix}-name`} className="form-label">
          {t('skillsTab.editor.nameLabel')}
          <span className="required-mark" aria-hidden="true">*</span>
        </label>
        <input
          id={`${idPrefix}-name`}
          type="text"
          value={value.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder={namePlaceholder ?? t('skillsTab.editor.namePlaceholder')}
          className="form-input"
          style={{ maxWidth: '100%' }}
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? `${idPrefix}-name-error` : undefined}
        />
        {errors.name && <FieldErrorMessage message={errors.name} />}
      </div>

      <div className="form-group">
        <label htmlFor={`${idPrefix}-type`} className="form-label">
          {t('skillsTab.editor.typeLabel')}
          {readOnlyAgent && (
            <>{' '}<InfoTooltip width={300}>{t('skillsTab.editor.readOnlyAgentTypeTooltip')}</InfoTooltip></>
          )}
        </label>
        <select
          id={`${idPrefix}-type`}
          value={readOnlyAgent ? 'Read' : value.type}
          onChange={(e) => onChange({ type: e.target.value as SkillType })}
          className="form-select"
          style={{ maxWidth: '100%' }}
          disabled={readOnlyAgent}
        >
          <option value="Read">{t('skillsTab.editor.typeRead')}</option>
          {!readOnlyAgent && (
            <option value="Execute">{t('skillsTab.editor.typeExecute')}</option>
          )}
        </select>
        {!readOnlyAgent && value.type === 'Execute' && (
          <div style={{ marginTop: 6, padding: '6px 10px', background: 'var(--warning-bg)', borderRadius: 6, fontSize: 12, color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span aria-hidden="true">⚠</span>
            {t('skillsTab.editor.executeWarning')}
          </div>
        )}
      </div>

      <div className={`form-group${errors.shortDescription ? ' form-group-invalid' : ''}`}>
        <label htmlFor={`${idPrefix}-shortdesc`} className="form-label">
          {t('skillsTab.editor.descriptionLabel')}
          <span className="required-mark" aria-hidden="true">*</span>
        </label>
        <input
          id={`${idPrefix}-shortdesc`}
          type="text"
          value={value.shortDescription}
          onChange={(e) => onChange({ shortDescription: e.target.value })}
          placeholder={t('skillsTab.editor.descriptionPlaceholder')}
          maxLength={70}
          className="form-input"
          style={{ maxWidth: '100%' }}
          aria-invalid={!!errors.shortDescription}
          aria-describedby={errors.shortDescription ? `${idPrefix}-shortdesc-error` : undefined}
        />
        {errors.shortDescription && <FieldErrorMessage message={errors.shortDescription} />}
      </div>

      <AgentSummaryField
        idPrefix={idPrefix}
        value={value.agentSummary}
        onChange={(agentSummary) => onChange({ agentSummary })}
        labelKey="skillsTab.editor.agentSummaryLabel"
        placeholderKey="skillsTab.editor.agentSummaryPlaceholder"
        tooltipKey="skillsTab.editor.agentSummaryTooltip"
        error={errors.agentSummary}
      />

      <div className={`form-group${errors.definition ? ' form-group-invalid' : ''}`}>
        <label htmlFor={`${idPrefix}-definition`} className="form-label">
          {t('skillsTab.editor.definitionLabel')}
          <span className="required-mark" aria-hidden="true">*</span>
        </label>
        <MdEditor
          id={`${idPrefix}-definition`}
          value={value.definition}
          onChange={(v) => onChange({ definition: v })}
          filename="skill.md"
          placeholder={'# Skill name\n\nDescribe what this skill does, what inputs it expects, and what it returns...'}
          invalidTypeMessage={t('markdownArea.invalidFileType')}
          uploadTitle={t('markdownArea.uploadTitle')}
          previewTitle={t('common.preview')}
          editTitle={t('agentDetail.identity.editSource')}
        />
        {errors.definition && <FieldErrorMessage message={errors.definition} />}
      </div>
    </>
  )
}
